import * as AvatarPrimitive from "@radix-ui/react-avatar"
import type * as React from "react"

import { cn } from "@/lib"

const sizeStyles: Record<"sm" | "md" | "lg", string> = {
  sm: "size-6 text-xs",
  md: "size-8 text-sm",
  lg: "size-10 text-base",
}

function AvatarRoot({
  className,
  size = "md",
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & {
  size?: "sm" | "md" | "lg"
}) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        "relative flex shrink-0 overflow-hidden rounded-full",
        sizeStyles[size],
        className,
      )}
      {...props}
    />
  )
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full object-cover", className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-beige-medium text-[12px] text-charcoal flex size-full items-center justify-center rounded-full font-medium",
        className,
      )}
      {...props}
    />
  )
}

export interface AvatarProps {
  src?: string
  alt?: string
  fallback?: string
  fallbackSrc?: string
  size?: "sm" | "md" | "lg"
  className?: string
}

function Avatar({
  src,
  alt = "Avatar",
  fallback,
  fallbackSrc,
  size = "md",
  className,
}: AvatarProps) {
  const initials = fallback
    ? fallback
        .split(" ")
        .map(name => name[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?"

  const imageSrc = src || fallbackSrc

  return (
    <AvatarRoot size={size} className={className}>
      {imageSrc && <AvatarImage src={imageSrc} alt={alt} />}
      <AvatarFallback>{initials}</AvatarFallback>
    </AvatarRoot>
  )
}

export { Avatar, AvatarFallback, AvatarImage, AvatarRoot }
